/*
 * BodyPark theme runtime.
 *
 * A direct port of the source project's React hooks and enhancer layer to
 * framework-free JavaScript. Behaviour, thresholds and state-class contracts
 * are unchanged; only the wiring differs (data-attributes instead of refs), so
 * the original stylesheets drive every visual state exactly as before.
 *
 * Everything re-initialises on Shopify's theme-editor events, so sections stay
 * interactive while a merchant edits them.
 */
(function () {
  'use strict';

  var EPSILON = 1;                       // sub-pixel slack for carousel offsets
  var SWIPE_THRESHOLD = 44;              // px before a drag counts as a swipe
  var SWIPE_VERTICAL_TOLERANCE = 1.15;   // how much more horizontal than vertical
  var MOBILE_QUERY = '(max-width: 790px)';
  var COMPACT_QUERY = '(max-width: 900px)';
  var NAV_PIN_OFFSET = 2;
  var DISMISSED_KEY = 'bodypark:announcement-dismissed-date';

  var cleanups = [];

  function on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    cleanups.push(function () {
      target.removeEventListener(type, handler, options);
    });
  }

  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function one(root, selector) {
    return (root || document).querySelector(selector);
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function todayStamp() {
    var now = new Date();
    var month = String(now.getMonth() + 1).padStart(2, '0');
    var day = String(now.getDate()).padStart(2, '0');
    return now.getFullYear() + '-' + month + '-' + day;
  }

  function money(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.themeMoneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ swipe */

  /**
   * Touch/pen swipe handling. Mouse pointers are ignored so text selection and
   * the arrow buttons keep working on desktop; a gesture only counts when it
   * travels past the threshold and stays meaningfully horizontal.
   */
  function bindSwipe(element, options) {
    var origin = null;

    on(element, 'pointerdown', function (event) {
      if (event.pointerType === 'mouse' || event.isPrimary === false) return;
      origin = {id: event.pointerId, x: event.clientX, y: event.clientY};
      // Capture is an optimisation, not a requirement: it throws if the pointer
      // is no longer active, and that must not cost us the swipe.
      try { if (element.setPointerCapture) element.setPointerCapture(event.pointerId); } catch (error) {}
    });

    on(element, 'pointercancel', function () {
      origin = null;
    });

    on(element, 'pointerup', function (event) {
      if (!origin || origin.id !== event.pointerId) return;
      var dx = event.clientX - origin.x;
      var dy = event.clientY - origin.y;
      origin = null;
      try { if (element.releasePointerCapture) element.releasePointerCapture(event.pointerId); } catch (error) {}

      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dy) > Math.abs(dx) * SWIPE_VERTICAL_TOLERANCE) return;

      if (dx < 0 && options.canNext()) options.onNext();
      else if (dx > 0 && options.canPrevious()) options.onPrevious();
    });
  }

  /* ------------------------------------------------------------- scroll lock */

  var lockCount = 0;
  var previousOverflow = '';

  function lockScroll() {
    if (lockCount === 0) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = previousOverflow;
  }

  /* -------------------------------------------------------------- navigation */

  /**
   * Pins the header once the page scrolls past the announcement bar, which is
   * when it takes on the frosted-glass treatment. The bar can be dismissed at
   * any time, so its height is re-read on mutation as well as scroll/resize.
   */
  function initNavigation() {
    var nav = one(document, '[data-bodypark-nav]');
    if (!nav) return;

    var menu = one(document, '.home-nav__mobile-menu');
    var toggle = one(nav, '[data-menu-toggle]');
    var frame = 0;

    function announcementHeight() {
      var bar = one(document, '.page-layout--announcement-visible .bodypark-announcement');
      return bar ? bar.offsetHeight : 0;
    }

    function evaluate() {
      frame = 0;
      var threshold = announcementHeight() + NAV_PIN_OFFSET;
      nav.classList.toggle('home-nav--fixed', window.scrollY >= threshold);
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(evaluate);
    }

    evaluate();
    on(window, 'scroll', schedule, {passive: true});
    on(window, 'resize', schedule);

    if ('MutationObserver' in window) {
      var observer = new MutationObserver(schedule);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });
      cleanups.push(function () {
        observer.disconnect();
      });
    }

    /* The hamburger is always live below 790px, but between 790 and 900px it
       only appears once the nav has pinned and the inline links collapsed. */
    function menuAvailable() {
      return window.matchMedia(MOBILE_QUERY).matches ||
        (nav.classList.contains('home-nav--fixed') && window.matchMedia(COMPACT_QUERY).matches);
    }

    var isOpen = false;

    function setMenu(open) {
      if (open === isOpen) return;
      isOpen = open;
      nav.classList.toggle('home-nav--menu-open', open);
      if (menu) menu.setAttribute('aria-hidden', String(!open));
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      }
      if (open) lockScroll();
      else unlockScroll();
    }

    if (toggle) {
      on(toggle, 'click', function () {
        if (menuAvailable()) setMenu(!isOpen);
      });
    }

    all(document, '[data-menu-close]').forEach(function (button) {
      on(button, 'click', function () {
        setMenu(false);
      });
    });

    on(window, 'keydown', function (event) {
      if (event.key === 'Escape' && isOpen) setMenu(false);
    });

    // Close the menu if the viewport grows past the point where it is reachable.
    var mobile = window.matchMedia(MOBILE_QUERY);
    var compact = window.matchMedia(COMPACT_QUERY);
    function check() {
      if (!menuAvailable()) setMenu(false);
    }
    mobile.addEventListener('change', check);
    compact.addEventListener('change', check);
    cleanups.push(function () {
      mobile.removeEventListener('change', check);
      compact.removeEventListener('change', check);
    });
  }

  /* ------------------------------------------------------------ announcement */

  function initAnnouncement() {
    var bar = one(document, '[data-announcement]');
    if (!bar) return;

    var layout = bar.closest('.page-layout');
    var messages = all(bar, '[data-announcement-message]');
    var index = 0;
    var paused = false;          // rotation stops for good once stepped manually
    var rotateMs = (parseFloat(bar.getAttribute('data-rotate')) || 5) * 1000;

    function render() {
      messages.forEach(function (message, i) {
        message.hidden = i !== index;
      });
    }

    function step(delta, manual) {
      if (manual) paused = true;
      index = (index + delta + messages.length) % messages.length;
      render();
    }

    all(bar, '[data-announcement-step]').forEach(function (button) {
      on(button, 'click', function () {
        step(parseInt(button.getAttribute('data-announcement-step'), 10), true);
      });
    });

    if (messages.length > 1) {
      var timer = window.setInterval(function () {
        if (!paused) step(1, false);
      }, rotateMs);
      cleanups.push(function () {
        window.clearInterval(timer);
      });
    }

    var close = one(bar, '[data-announcement-close]');
    if (close && layout) {
      on(close, 'click', function () {
        layout.classList.add('page-layout--announcement-closing');
        try {
          window.localStorage.setItem(DISMISSED_KEY, todayStamp());
        } catch (error) {
          /* Private browsing: the bar still closes for this session. */
        }
        var finish = function (event) {
          if (event && event.target !== bar) return;
          bar.remove();
          layout.classList.remove('page-layout--announcement-visible');
          layout.classList.remove('page-layout--announcement-closing');
          document.documentElement.classList.add('bodypark-announcement-dismissed-today');
        };
        bar.addEventListener('transitionend', function (event) {
          if (event.propertyName === 'height') finish(event);
        }, {once: true});
        window.setTimeout(finish, 600);   // fallback if the transition is skipped
      });
    }

    render();
  }

  /* --------------------------------------------------------------- overlays */

  function bindOverlay(overlay, openSelectors, closeSelector) {
    if (!overlay) return null;

    function open() {
      overlay.classList.add('expanded');
      lockScroll();
    }

    function close() {
      if (!overlay.classList.contains('expanded')) return;
      overlay.classList.remove('expanded');
      unlockScroll();
    }

    all(document, openSelectors).forEach(function (button) {
      on(button, 'click', function (event) {
        event.preventDefault();
        open();
      });
    });

    all(overlay, closeSelector).forEach(function (button) {
      on(button, 'click', close);
    });

    on(document, 'keydown', function (event) {
      if (event.key === 'Escape') close();
    });

    return {open: open, close: close};
  }

  /* The drawer's markup is replaced wholesale every time the cart section is
   * re-rendered, so nothing may hold on to a node across that swap: a captured
   * element becomes detached and its handlers then toggle a node that is no
   * longer in the document. Everything below resolves the overlay at call time
   * and listens on `document`, so one binding survives every re-render. */

  function cartOverlay() {
    return one(document, '[data-cart-overlay]');
  }

  function openCart() {
    var overlay = cartOverlay();
    if (!overlay || overlay.classList.contains('expanded')) return;
    overlay.classList.add('expanded');
    lockScroll();
  }

  function closeCart() {
    var overlay = cartOverlay();
    if (!overlay || !overlay.classList.contains('expanded')) return;
    overlay.classList.remove('expanded');
    unlockScroll();
  }

  function initCart() {
    if (!cartOverlay()) return;

    on(document, 'click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var toggle = target.closest('[data-cart-toggle]');
      if (toggle) { event.preventDefault(); openCart(); return; }

      if (target.closest('[data-cart-close]')) { closeCart(); return; }

      // Quantity controls post to Shopify, then re-render the drawer section.
      var step = target.closest('[data-qty-change]');
      var overlay = cartOverlay();
      if (step && overlay && overlay.contains(step)) {
        step.setAttribute('aria-busy', 'true');
        changeLine(step.getAttribute('data-qty-change'), step.getAttribute('data-qty'));
      }
    });

    on(document, 'keydown', function (event) {
      if (event.key === 'Escape') closeCart();
    });
  }

  function refreshCartSection() {
    return fetch(window.Shopify && window.Shopify.routes ?
      window.Shopify.routes.root + '?sections=cart-drawer' : '/?sections=cart-drawer')
      .then(function (response) { return response.json(); })
      .then(function (data) {
        var holder = document.getElementById('shopify-section-cart-drawer');
        if (!holder || !data['cart-drawer']) return;
        var wasOpen = !!one(holder, '.overlay.expanded');
        var parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
        var fresh = parsed.getElementById('shopify-section-cart-drawer');
        holder.innerHTML = fresh ? fresh.innerHTML : data['cart-drawer'];
        if (wasOpen) {
          var overlay = one(holder, '[data-cart-overlay]');
          if (overlay) overlay.classList.add('expanded');
        }
        rebindCartDrawer(wasOpen);
        updateCartCount();
      });
  }

  /* Listeners are delegated on `document`, so a re-rendered drawer needs no
   * rebinding -- only its open/closed state has to be settled. */
  function rebindCartDrawer(keepOpen) {
    if (!keepOpen) closeCart();
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({id: key, quantity: parseInt(quantity, 10)}),
    })
      .then(function (response) { return response.json(); })
      .then(refreshCartSection)
      .catch(function () { window.location.href = '/cart'; });
  }

  function updateCartCount() {
    fetch('/cart.js', {headers: {Accept: 'application/json'}})
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        all(document, '[data-cart-count]').forEach(function (badge) {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  /** Add-to-cart forms post through AJAX so the drawer can open in place. */
  function initAddToCart() {
    all(document, 'form[action*="/cart/add"]').forEach(function (form) {
      on(form, 'submit', function (event) {
        var drawer = one(document, '[data-cart-overlay]');
        if (!drawer) return;                       // no drawer: let it post normally
        event.preventDefault();
        var button = one(form, '[type="submit"]');
        if (button) button.setAttribute('aria-busy', 'true');

        fetch('/cart/add.js', {method: 'POST', body: new FormData(form)})
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            return response.json();
          })
          .then(function () { return refreshCartSection(); })
          .then(function () { openCart(); })
          .catch(function () { form.submit(); })
          .finally(function () {
            if (button) button.removeAttribute('aria-busy');
          });
      });
    });
  }

  /* ----------------------------------------------------------------- search */

  function initSearch() {
    var overlay = one(document, '[data-search-overlay]');
    bindOverlay(overlay, '[data-search-toggle]', '[data-search-close]');
    if (!overlay) return;

    var input = one(overlay, '[data-predictive-input]');
    var results = one(overlay, '[data-predictive-results]');
    if (!input || !results) return;

    var timer = null;

    function render(products) {
      if (!products.length) {
        results.innerHTML = '<p class="predictive-search__empty">No matches yet.</p>';
        return;
      }
      results.innerHTML = products.map(function (product) {
        var image = product.featured_image && product.featured_image.url
          ? '<img src="' + product.featured_image.url + '" alt="" loading="lazy">'
          : '';
        return '<a class="predictive-search__result" href="' + product.url + '">' + image +
          '<span class="predictive-search__result-title">' + product.title + '</span>' +
          '<span class="predictive-search__result-price">' + (product.price ? money(product.price) : '') + '</span></a>';
      }).join('');
    }

    on(input, 'input', function () {
      var term = input.value.trim();
      window.clearTimeout(timer);
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = window.setTimeout(function () {
        fetch('/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last')
          .then(function (response) { return response.json(); })
          .then(function (data) {
            render((data.resources && data.resources.results && data.resources.results.products) || []);
          })
          .catch(function () {});
      }, 220);
    });
  }

  /* ------------------------------------------------------------- hero promo */

  function initHeroPromo() {
    var close = one(document, '[data-hero-promo-close]');
    if (!close) return;
    on(close, 'click', function () {
      var promo = close.closest('[data-hero-promo]');
      if (promo) promo.remove();
    });
  }

  /* ------------------------------------------------------------- home video */

  function initHomeVideo() {
    var section = one(document, '[data-home-video]');
    if (!section) return;

    var background = one(section, '[data-home-video-bg]');
    var dialog = one(document, '[data-home-video-dialog]');
    var feature = dialog ? one(dialog, '[data-home-video-feature]') : null;
    var dialogOpen = false;

    if (background) {
      // The muted loop only runs while the band is on screen.
      var observer = new IntersectionObserver(function (entries) {
        var visible = entries[0] && entries[0].isIntersecting;
        if (visible && !dialogOpen) {
          background.muted = true;
          var promise = background.play();
          if (promise && promise.catch) promise.catch(function () {});
        } else {
          background.pause();
        }
      }, {threshold: 0.35});
      observer.observe(background);
      cleanups.push(function () { observer.disconnect(); });
    }

    if (!dialog || !feature) return;

    function openDialog() {
      dialogOpen = true;
      if (background) background.pause();
      dialog.hidden = false;
      lockScroll();
      feature.currentTime = 0;
      feature.muted = false;
      var promise = feature.play();
      if (promise && promise.catch) promise.catch(function () {});
    }

    function closeDialog() {
      if (!dialogOpen) return;
      dialogOpen = false;
      feature.pause();
      feature.currentTime = 0;
      dialog.hidden = true;
      unlockScroll();
    }

    all(document, '[data-home-video-open]').forEach(function (button) {
      on(button, 'click', openDialog);
    });
    all(dialog, '[data-home-video-close]').forEach(function (button) {
      on(button, 'click', closeDialog);
    });
    on(document, 'keydown', function (event) {
      if (event.key === 'Escape') closeDialog();
    });
  }

  /* --------------------------------------------------------------- show up */

  function initShowUp() {
    var section = one(document, '[data-show-up]');
    if (!section) return;

    var carousel = one(section, '[data-show-up-carousel]');
    if (carousel) {
      var data = one(carousel, '[data-show-up-slides]');
      var slides = [];
      try {
        slides = JSON.parse(data.textContent);
      } catch (error) {
        slides = [];
      }

      if (slides.length > 1) {
        var main = one(carousel, '[data-show-up-main]');
        var peek = one(carousel, '[data-show-up-peek]');
        var index = 0;

        function go(delta) {
          index = (index + delta + slides.length) % slides.length;
          var next = slides[(index + 1) % slides.length];
          main.src = slides[index].src;
          main.alt = slides[index].alt || '';
          if (peek) peek.src = next.src;
          // Restart the slide-in by re-adding the directional class.
          main.classList.remove('home-show-up__slide-image--from-next', 'home-show-up__slide-image--from-prev');
          void main.offsetWidth;
          main.classList.add(delta > 0
            ? 'home-show-up__slide-image--from-next'
            : 'home-show-up__slide-image--from-prev');
        }

        all(carousel, '[data-show-up-step]').forEach(function (button) {
          on(button, 'click', function () {
            go(parseInt(button.getAttribute('data-show-up-step'), 10));
          });
        });

        bindSwipe(carousel, {
          canNext: function () { return true; },
          canPrevious: function () { return true; },
          onNext: function () { go(1); },
          onPrevious: function () { go(-1); },
        });
      }
    }

    initShowUpPin(section);
  }

  /**
   * Mobile only: the stage pins while the copy scrolls through its own
   * viewport, the backdrop cross-fades at the halfway point, and a slim thumb
   * tracks the pinned scroll. Desktop keeps the static two-column layout.
   */
  function initShowUpPin(section) {
    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    var media = gsap.matchMedia();
    media.add(MOBILE_QUERY, function () {
      var scrolly = one(section, '.home-show-up__mobile-scrolly');
      var pin = one(section, '.home-show-up__mobile-pin');
      var copy = one(section, '.home-show-up__mobile-copy');
      var viewport = one(section, '.home-show-up__mobile-viewport');
      var track = one(section, '.home-show-up__mobile-copy-track');
      var scrollbar = one(section, '.home-show-up__mobile-scrollbar');
      var thumb = one(section, '.home-show-up__mobile-scrollbar-thumb');
      if (!scrolly || !pin || !copy || !viewport || !track || !scrollbar || !thumb) return;

      var reduce = reducedMotion();

      function render(progress) {
        var fade = progress <= 0.05 ? 'start' : progress >= 0.95 ? 'end' : 'mid';
        if (copy.dataset.fade !== fade) copy.dataset.fade = fade;

        var backdrop = progress >= 0.5 ? 'b' : 'a';
        if (pin.dataset.bg !== backdrop) pin.dataset.bg = backdrop;

        scrollbar.style.opacity = progress > 0.06 && progress < 0.94 ? '0.3' : '0';
      }

      function copyDistance() {
        return Math.max(0, track.scrollHeight - viewport.clientHeight);
      }
      function thumbDistance() {
        return Math.max(0, scrollbar.clientHeight - thumb.clientHeight);
      }

      pin.dataset.bg = 'a';
      gsap.set([track, thumb], {y: 0});
      render(0);

      var timeline = gsap.timeline({
        defaults: {ease: 'none'},
        scrollTrigger: {
          trigger: scrolly,
          start: 'top top',
          end: function () { return '+=' + Math.round(pin.offsetHeight * 1.6); },
          pin: pin,
          scrub: reduce ? true : 0.35,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: function (self) { render(self.progress); },
        },
      });

      timeline
        .fromTo(track, {y: 0}, {y: function () { return -copyDistance(); }, immediateRender: false}, 0)
        .fromTo(thumb, {y: 0}, {y: thumbDistance, immediateRender: false}, 0);

      // The pinned distance depends on image height, so re-measure on load.
      all(section, '.home-show-up__mobile-bg').forEach(function (image) {
        if (image.complete) ScrollTrigger.refresh();
        else image.addEventListener('load', function () { ScrollTrigger.refresh(); }, {once: true});
      });

      return function () {
        if (timeline.scrollTrigger) timeline.scrollTrigger.kill();
        timeline.kill();
        delete pin.dataset.bg;
        scrollbar.style.opacity = '';
      };
    });

    cleanups.push(function () { media.revert(); });
  }

  /* ------------------------------------------------------- track carousels */

  /**
   * Horizontally translating card tracks. Each step lands on a card's
   * offsetLeft, clamped so the last step flushes against the right edge rather
   * than leaving a gap. Offsets are re-measured on resize because the card
   * widths are viewport-relative.
   */
  function initTrackCarousel(root) {
    var viewport = one(root, '[data-track-viewport]');
    var track = one(root, '[data-track]');
    var cards = all(root, '[data-track-card]');
    var previous = one(root, '[data-track-step="-1"]');
    var next = one(root, '[data-track-step="1"]');
    if (!viewport || !track || cards.length < 2) return;

    var index = 0;

    function measureMax() {
      return Math.max(0, track.scrollWidth - viewport.clientWidth);
    }

    function offsetFor(i, max) {
      var card = cards[i];
      if (!card) return 0;
      if (i === cards.length - 1) return max;   // last card settles flush right
      return Math.min(card.offsetLeft, max);
    }

    function sync() {
      var max = measureMax();
      var offset = offsetFor(index, max);
      track.style.transform = 'translate3d(-' + offset + 'px, 0, 0)';
      if (previous) previous.disabled = offset <= EPSILON;
      if (next) next.disabled = !(max > EPSILON && offset < max - EPSILON);
    }

    function moveNext() {
      var max = measureMax();
      var current = offsetFor(index, max);
      if (current >= max - EPSILON) { index = cards.length - 1; sync(); return; }
      for (var i = index + 1; i < cards.length; i++) {
        if (offsetFor(i, max) > current + EPSILON) { index = i; sync(); return; }
      }
      index = cards.length - 1;
      sync();
    }

    function movePrevious() {
      var max = measureMax();
      var current = offsetFor(index, max);
      if (current <= EPSILON) { index = 0; sync(); return; }
      for (var i = index - 1; i >= 0; i--) {
        if (offsetFor(i, max) < current - EPSILON) { index = i; sync(); return; }
      }
      index = 0;
      sync();
    }

    if (previous) on(previous, 'click', movePrevious);
    if (next) on(next, 'click', moveNext);

    bindSwipe(viewport, {
      canNext: function () {
        var max = measureMax();
        return max > EPSILON && offsetFor(index, max) < max - EPSILON;
      },
      canPrevious: function () { return offsetFor(index, measureMax()) > EPSILON; },
      onNext: moveNext,
      onPrevious: movePrevious,
    });

    var observer = new ResizeObserver(sync);
    observer.observe(viewport);
    observer.observe(track);
    cleanups.push(function () { observer.disconnect(); });

    sync();
  }

  /* ------------------------------------------------------ workout companion */

  function initWorkoutCompanion() {
    var section = one(document, '[data-companion]');
    if (!section) return;

    var frame = one(section, '[data-companion-frame]');
    var desktop = one(section, '[data-companion-desktop]');
    var mobile = one(section, '[data-companion-mobile]');
    var widget = one(section, '[data-companion-widget]');
    var slides = [];
    try {
      slides = JSON.parse(one(section, '[data-companion-slides]').textContent);
    } catch (error) {
      return;
    }
    if (slides.length < 2) return;

    var index = 0;
    var arrows = all(section, '[data-companion-step]');

    function render() {
      var slide = slides[index];
      if (desktop) { desktop.src = slide.desktop; desktop.alt = slide.alt || ''; }
      if (mobile) { mobile.src = slide.mobile; mobile.alt = slide.alt || ''; }
      // The Pomodoro widget only overlays the first scene.
      if (widget) widget.hidden = index !== 0;
      if (frame) {
        frame.setAttribute('data-slide-position',
          index === 0 ? 'first' : index === slides.length - 1 ? 'last' : 'middle');
      }
      arrows.forEach(function (button) {
        var delta = parseInt(button.getAttribute('data-companion-step'), 10);
        button.disabled = delta > 0 ? index >= slides.length - 1 : index <= 0;
      });
    }

    function go(delta) {
      index = Math.min(Math.max(index + delta, 0), slides.length - 1);
      render();
    }

    arrows.forEach(function (button) {
      on(button, 'click', function () {
        go(parseInt(button.getAttribute('data-companion-step'), 10));
      });
    });

    if (frame) {
      bindSwipe(frame, {
        canNext: function () { return index < slides.length - 1; },
        canPrevious: function () { return index > 0; },
        onNext: function () { go(1); },
        onPrevious: function () { go(-1); },
      });
    }

    render();
  }

  /* --------------------------------------------------------- video testimonials */

  /** Tap-to-play testimonial clips; the source is attached on first play. */
  function initCustomerVideos() {
    all(document, '.product-detail-customer-videos__card').forEach(function (card) {
      var video = one(card, 'video');
      var button = one(card, '.product-detail-customer-videos__play');
      if (!video || !button) return;

      function sync() {
        button.classList.toggle('product-detail-customer-videos__play--active', !video.paused);
      }

      on(button, 'click', function () {
        if (video.paused) {
          var source = video.getAttribute('data-src');
          if (source && !video.src) video.src = source;
          all(document, 'video').forEach(function (other) {
            if (other !== video) other.pause();
          });
          var promise = video.play();
          if (promise && promise.catch) promise.catch(function () {});
        } else {
          video.pause();
        }
      });

      on(video, 'play', sync);
      on(video, 'pause', sync);
      on(video, 'ended', sync);
    });
  }

  /* ------------------------------------------------------------- enhancers --
   * Behaviour for the ported marketing pages, whose markup mirrors the source
   * project's rendered output. Each one wires the state contract the
   * stylesheets already express (aria-current, data-active, is-active, …).
   */

  function enhanceSwatchGroups(root) {
    var selectors = ['.atom-companion__swatches', '.accessories-card__swatches', '.accessories-mobile-card__swatches'];
    selectors.forEach(function (selector) {
      all(root, selector).forEach(function (group) {
        var scope = group.closest('.atom-companion__color, .atom-companion__panel, .accessories-card, .accessories-mobile-card, section, article');
        if (!scope) return;
        var images = all(scope, '[data-active], .atom-companion__motion-product, .accessories-card__image');
        if (images.length < 2) return;
        var buttons = all(group, 'button');

        buttons.forEach(function (button, index) {
          on(button, 'click', function () {
            buttons.forEach(function (other, i) {
              other.setAttribute('aria-pressed', String(i === index));
            });
            images.forEach(function (image, i) {
              if (i === index) image.setAttribute('data-active', 'true');
              else image.removeAttribute('data-active');
            });
            var label = (button.getAttribute('aria-label') || '').replace(/^Show\s+/, '');
            var heading = group.parentElement && group.parentElement.querySelector('h3');
            if (label && heading) heading.textContent = label.split(' ').pop() || label;
          });
        });
      });
    });
  }

  /** Pinned feature showcases that step through their list as the page scrolls. */
  function enhanceShowcases(root) {
    all(root, '.atom-showcase').forEach(function (showcase) {
      var items = all(showcase, '.atom-showcase__item');
      var layers = all(showcase, '.atom-showcase__media-layer');
      var mediaImages = all(showcase, '.atom-showcase__media > img');
      var progress = one(showcase, '.atom-showcase__progress-bar') || one(showcase, '.atom-showcase__progress span');
      var steps = Math.max(items.length, layers.length, mediaImages.length);
      if (steps < 2) return;

      var current = -1;
      var frame = 0;

      function apply(index, ratio) {
        if (index !== current) {
          current = index;
          items.forEach(function (item, i) {
            if (i === index) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          });
          var list = layers.length ? layers : mediaImages;
          list.forEach(function (element, i) {
            if (i === index) {
              element.setAttribute('data-active', 'true');
              element.removeAttribute('aria-hidden');
            } else {
              element.removeAttribute('data-active');
              element.setAttribute('aria-hidden', 'true');
            }
          });
        }
        if (progress) progress.style.setProperty('--atom-showcase-progress', ratio.toFixed(4));
      }

      function update() {
        frame = 0;
        var rect = showcase.getBoundingClientRect();
        var travel = Math.max(1, showcase.offsetHeight - window.innerHeight);
        var ratio = Math.min(Math.max(-rect.top / travel, 0), 1);
        apply(Math.min(steps - 1, Math.floor(ratio * steps)), ratio);
      }

      function schedule() {
        if (!frame) frame = window.requestAnimationFrame(update);
      }

      update();
      on(window, 'scroll', schedule, {passive: true});
      on(window, 'resize', schedule);

      items.forEach(function (item, index) {
        on(item, 'click', function () {
          var travel = Math.max(1, showcase.offsetHeight - window.innerHeight);
          var top = showcase.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: top + (travel * (index + 0.5)) / steps,
            behavior: reducedMotion() ? 'auto' : 'smooth',
          });
        });
      });
    });
  }

  /** Arrow-driven slide tracks used on the Atom and Tech pages. */
  function enhanceMobileCarousels(root) {
    all(root, '.atom-mobile-pinned-carousel').forEach(function (carousel) {
      var track = one(carousel, '.atom-mobile-pinned-carousel__track');
      var slides = all(carousel, '.atom-mobile-pinned-carousel__slide');
      var buttons = all(carousel, '.atom-mobile-pinned-carousel__arrows button');
      var icons = all(carousel, '.atom-mobile-pinned-carousel__icon-list button');
      if (!track || slides.length < 2) return;

      var index = 0;
      var previous = buttons[0];
      var next = buttons[1];

      function render() {
        var offset = slides[index].offsetLeft - slides[0].offsetLeft;
        track.style.transform = 'translate3d(-' + offset + 'px, 0, 0)';
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
        icons.forEach(function (icon, i) {
          if (i === index) icon.setAttribute('aria-current', 'true');
          else icon.removeAttribute('aria-current');
        });
      }

      function goTo(value) {
        index = Math.min(Math.max(value, 0), slides.length - 1);
        render();
      }

      if (previous) on(previous, 'click', function () { goTo(index - 1); });
      if (next) on(next, 'click', function () { goTo(index + 1); });
      icons.forEach(function (icon, i) {
        on(icon, 'click', function () { goTo(i); });
      });

      bindSwipe(carousel, {
        canNext: function () { return index < slides.length - 1; },
        canPrevious: function () { return index > 0; },
        onNext: function () { goTo(index + 1); },
        onPrevious: function () { goTo(index - 1); },
      });

      var observer = new ResizeObserver(render);
      observer.observe(carousel);
      cleanups.push(function () { observer.disconnect(); });
      render();
    });
  }

  /** FAQ accordion on the product page.
   *  Each question lives inside an <h5>, so the answer is a sibling of that
   *  heading rather than of the button -- it has to be resolved from the item.
   *  Items toggle independently and several can stay open, matching the source
   *  site. <button> gives Enter/Space for free. */
  function enhanceFaqs(root) {
    all(root, '.product-detail-faqs__question').forEach(function (question) {
      var item = question.closest('.product-detail-faqs__item');
      var answer = item ? one(item, '.product-detail-faqs__answer') : null;
      if (!answer) return;

      if (!answer.id) answer.id = 'faq-answer-' + (faqAnswerSeq += 1);
      question.setAttribute('aria-controls', answer.id);

      // Trust the markup's initial state, then keep the two in step.
      var expanded = question.getAttribute('aria-expanded') === 'true';
      answer.hidden = !expanded;

      on(question, 'click', function () {
        var open = question.getAttribute('aria-expanded') === 'true';
        question.setAttribute('aria-expanded', String(!open));
        answer.hidden = open;
      });
    });
  }
  var faqAnswerSeq = 0;

  /** Sticky section tabs that scroll to their matching panel. */
  function enhanceProductTabs(root) {
    var buttons = all(root, '.product-detail-tabs__bar button');
    var sections = all(root, '.product-detail-tabs__section');
    if (!buttons.length) return;

    buttons.forEach(function (button, index) {
      on(button, 'click', function () {
        buttons.forEach(function (other, i) {
          other.setAttribute('aria-selected', String(i === index));
        });
        if (sections[index]) {
          sections[index].scrollIntoView({
            behavior: reducedMotion() ? 'auto' : 'smooth',
            block: 'start',
          });
        }
      });
    });
  }

  /** Small image carousels inside the store feature panels. */
  function enhanceStoreFeatureCarousels(root) {
    all(root, '.product-detail-store-feature__media-frame--carousel').forEach(function (frame) {
      var images = all(frame, '.product-detail-store-feature__media-image');
      var buttons = all(frame, '.product-detail-store-feature__media-carousel-arrow');
      if (images.length < 2) return;

      var index = Math.max(0, images.findIndex(function (image) {
        return image.classList.contains('product-detail-store-feature__media-image--active');
      }));

      function render() {
        images.forEach(function (image, i) {
          image.classList.toggle('product-detail-store-feature__media-image--active', i === index);
        });
      }
      function goTo(value) {
        index = (value + images.length) % images.length;
        render();
      }

      if (buttons[0]) on(buttons[0], 'click', function () { goTo(index - 1); });
      if (buttons[1]) on(buttons[1], 'click', function () { goTo(index + 1); });
      render();
    });
  }

  /** Accessories mobile card carousel. */
  function enhanceAccessoriesMobile(root) {
    all(root, '.accessories-mobile').forEach(function (section) {
      var track = one(section, '.accessories-mobile__track');
      var cards = all(section, '.accessories-mobile-card');
      var buttons = all(section, '.accessories-mobile__carousel-button');
      if (!track || cards.length < 2) return;

      var index = 0;
      function render() {
        var offset = cards[index].offsetLeft - cards[0].offsetLeft;
        track.style.transform = 'translate3d(-' + offset + 'px, 0, 0)';
        if (buttons[0]) buttons[0].disabled = index === 0;
        if (buttons[1]) buttons[1].disabled = index === cards.length - 1;
      }
      function goTo(value) {
        index = Math.min(Math.max(value, 0), cards.length - 1);
        render();
      }

      if (buttons[0]) on(buttons[0], 'click', function () { goTo(index - 1); });
      if (buttons[1]) on(buttons[1], 'click', function () { goTo(index + 1); });

      var observer = new ResizeObserver(render);
      observer.observe(section);
      cleanups.push(function () { observer.disconnect(); });
      render();
    });
  }

  /** News list category filter, with the selection mirrored into the URL. */
  function enhanceNewsTabs(root) {
    var tabs = all(root, '.news-list-tabs__item');
    var items = all(root, '.news-list-item');
    var status = one(root, '.news-list-statusbar p');
    if (!tabs.length || !items.length) return;

    var total = parseInt(((status && status.textContent) || '').replace(/.*of (\d+).*/, '$1'), 10) || items.length;

    function select(index, pushUrl) {
      var tag = (tabs[index].getAttribute('data-filter') || '').toLowerCase();
      var shown = 0;
      tabs.forEach(function (tab, i) {
        tab.dataset.active = String(i === index);
        if (i === index) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
      });
      items.forEach(function (item) {
        var category = (item.getAttribute('data-category') || '').toLowerCase();
        var match = !tag || category === tag;
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (status) status.textContent = shown + ' of ' + total + ' articles';
      if (pushUrl) {
        window.history.replaceState(null, '', tag ? '?tag=' + encodeURIComponent(tag) : window.location.pathname);
      }
    }

    tabs.forEach(function (tab, index) {
      on(tab, 'click', function (event) {
        event.preventDefault();
        select(index, true);
      });
    });

    var initial = new URLSearchParams(window.location.search).get('tag');
    if (initial) {
      var found = tabs.findIndex(function (tab) {
        return (tab.getAttribute('data-filter') || '').toLowerCase() === initial.toLowerCase();
      });
      if (found >= 0) select(found, false);
    }
  }

  /** "Back to TOP" pill on the news list. */
  function enhanceBackToTop(root) {
    var button = one(root, '.news-list-backtop');
    if (!button) return;
    on(button, 'click', function () {
      window.scrollTo({top: 0, behavior: reducedMotion() ? 'auto' : 'smooth'});
    });
  }

  /** Reveal-on-scroll for the progress bars on the Atom "why" section. */
  function enhanceWhyBars(root) {
    var bars = all(root, '.atom-why__bar');
    if (!bars.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('atom-why__bar--active');
          observer.unobserve(entry.target);
        }
      });
    }, {threshold: 0.4});
    bars.forEach(function (bar) { observer.observe(bar); });
    cleanups.push(function () { observer.disconnect(); });
  }

  var ENHANCERS = [
    enhanceSwatchGroups,
    enhanceShowcases,
    enhanceMobileCarousels,
    enhanceFaqs,
    enhanceProductTabs,
    enhanceStoreFeatureCarousels,
    enhanceAccessoriesMobile,
    enhanceNewsTabs,
    enhanceBackToTop,
    enhanceWhyBars,
  ];


  /* ------------------------------------------------------------ product page */

  /**
   * Product gallery: thumbnails, dots and arrows all drive the same active
   * slide. Mirrors the source project's gallery enhancer.
   */
  var productGalleryApi = null;
  var galleryLightboxOpen = false;

  function initProductGallery() {
    var gallery = one(document, '[data-product-gallery]');
    if (!gallery) return;

    var slides = all(gallery, '.product-detail-gallery__static-slide');
    var track = one(gallery, '[data-gallery-track]');
    var thumbs = all(gallery, '.product-detail-gallery__thumb');
    var dots = all(gallery, '.product-detail-gallery__dot');
    var count = one(gallery, '[data-gallery-count]');
    if (slides.length < 2) return;

    var index = Math.max(0, slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    }));

    function render() {
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      [thumbs, dots].forEach(function (group) {
        group.forEach(function (button, i) {
          if (i === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
      });
      if (track) track.style.transform = 'translateX(calc(' + (index * -100) + '% + 0px))';
      if (count) count.textContent = (index + 1) + ' / ' + slides.length;
    }

    function goTo(value) {
      index = (value + slides.length) % slides.length;
      render();
    }

    thumbs.concat(dots).forEach(function (button) {
      on(button, 'click', function () {
        goTo(parseInt(button.getAttribute('data-gallery-go'), 10));
      });
    });

    all(gallery, '[data-gallery-step]').forEach(function (button) {
      on(button, 'click', function () {
        goTo(index + parseInt(button.getAttribute('data-gallery-step'), 10));
      });
    });

    bindSwipe(gallery, {
      // The lightbox is a descendant, so without this a swipe inside it would
      // advance the gallery as well and the two would step twice.
      canNext: function () { return !galleryLightboxOpen; },
      canPrevious: function () { return !galleryLightboxOpen; },
      onNext: function () { goTo(index + 1); },
      onPrevious: function () { goTo(index - 1); },
    });

    productGalleryApi = {goTo: goTo};
    render();

    initGalleryLightbox(gallery, slides, {
      current: function () { return index; },
      goTo: goTo,
    });
  }

  /**
   * Enlarged view for the gallery.
   *
   * The reference site's gallery images are inert -- clicking one does nothing
   * there -- so this behaviour is an addition rather than a reproduction. It
   * reads its images from the static slides, which carry a 2400px source, so
   * the gallery stays the single source of truth and the two cannot drift.
   */
  function initGalleryLightbox(gallery, slides, galleryApi) {
    var viewport = one(gallery, '[data-gallery-zoom]');
    var box = one(gallery, '[data-gallery-lightbox]');
    var image = box ? one(box, '[data-gallery-lightbox-image]') : null;
    if (!viewport || !box || !image || !slides.length) return;

    var dialog = one(box, '[data-gallery-lightbox-dialog]');
    var counter = one(box, '[data-gallery-lightbox-count]');
    var closeButton = one(box, '[data-gallery-lightbox-close]');
    var index = 0;
    var open = false;
    var lastFocused = null;

    var sources = slides.map(function (slide) {
      var img = one(slide, 'img');
      return {
        src: img ? (img.getAttribute('data-gallery-full') || img.currentSrc || img.src) : '',
        alt: img ? (img.getAttribute('alt') || '') : '',
        width: img ? img.getAttribute('data-gallery-full-width') : null,
        height: img ? img.getAttribute('data-gallery-full-height') : null,
      };
    });

    function show(value) {
      index = (value + sources.length) % sources.length;
      var item = sources[index];
      image.classList.remove('is-loaded');
      if (image.getAttribute('src') !== item.src) image.setAttribute('src', item.src);
      image.setAttribute('alt', item.alt);
      // Each image keeps its own intrinsic ratio, so nothing jumps while loading.
      if (item.width && item.height) {
        image.setAttribute('width', item.width);
        image.setAttribute('height', item.height);
      }
      if (image.complete && image.naturalWidth) image.classList.add('is-loaded');
      if (counter) counter.textContent = (index + 1) + ' / ' + sources.length;
      // Leave the gallery on whatever the viewer last looked at.
      if (galleryApi.current() !== index) galleryApi.goTo(index);
    }

    function openBox(startIndex) {
      if (open) return;
      open = true;
      galleryLightboxOpen = true;
      lastFocused = document.activeElement;
      box.hidden = false;
      show(startIndex);
      lockScroll();
      // Let `hidden` clear before the opacity transition starts.
      requestAnimationFrame(function () { box.classList.add('is-open'); });
      if (closeButton) closeButton.focus();
    }

    function closeBox() {
      if (!open) return;
      open = false;
      galleryLightboxOpen = false;
      box.classList.remove('is-open');
      unlockScroll();
      // Hide only once the fade has run; re-opening in the meantime cancels it.
      if (reducedMotion()) box.hidden = true;
      else window.setTimeout(function () { if (!open) box.hidden = true; }, 220);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
      lastFocused = null;
    }

    on(image, 'load', function () { image.classList.add('is-loaded'); });

    /* Open on click, but never when the pointer was dragged -- a swipe on
     * touch still emits a click, and that must not pop the lightbox open. */
    var down = null;
    on(viewport, 'pointerdown', function (event) { down = {x: event.clientX, y: event.clientY}; });
    on(viewport, 'pointercancel', function () { down = null; });
    on(viewport, 'click', function (event) {
      var moved = down && (Math.abs(event.clientX - down.x) > 8 || Math.abs(event.clientY - down.y) > 8);
      down = null;
      if (moved) return;
      if (event.target.closest('button, a')) return;   // arrows keep their own job
      openBox(galleryApi.current());
    });

    // Keyboard parity for the image itself: the thumbnails and dots are already
    // buttons, so the viewport is the only part that needs its own affordance.
    viewport.setAttribute('tabindex', '0');
    viewport.setAttribute('role', 'button');
    on(viewport, 'keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target !== viewport) return;
      event.preventDefault();
      openBox(galleryApi.current());
    });

    all(box, '[data-gallery-lightbox-step]').forEach(function (button) {
      on(button, 'click', function (event) {
        event.stopPropagation();
        show(index + parseInt(button.getAttribute('data-gallery-lightbox-step'), 10));
      });
    });

    all(box, '[data-gallery-lightbox-close], [data-gallery-lightbox-dismiss]').forEach(function (target) {
      on(target, 'click', closeBox);
    });

    // The backdrop sits behind the dialog, so a click that lands on the dialog
    // padding should dismiss too -- but one on the image must not.
    if (dialog) {
      on(dialog, 'click', function (event) {
        if (event.target === dialog || event.target.classList.contains('product-gallery-lightbox__figure')) closeBox();
      });
    }

    on(document, 'keydown', function (event) {
      if (!open) return;
      if (event.key === 'Escape') { closeBox(); return; }
      if (sources.length < 2) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
    });

    // Keep focus inside the dialog while it is open.
    on(box, 'keydown', function (event) {
      if (event.key !== 'Tab' || !open) return;
      var focusable = all(box, 'button:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    if (sources.length > 1) {
      bindSwipe(box, {
        canNext: function () { return open; },
        canPrevious: function () { return open; },
        onNext: function () { show(index + 1); },
        onPrevious: function () { show(index - 1); },
      });
    }
  }

  /**
   * Package cards act as the variant picker. Selecting one updates the hidden
   * form input, the accelerated-checkout permalink, the displayed price and the
   * ?variant= query parameter, the way a Shopify product page is expected to.
   */
  /**
   * Package picker + the conditional cap-colour swatches.
   *
   * The reference reloads the page for every combination
   * (?Package=...&Styling+Cap+Color=...). This drives the same state changes
   * client-side: card selection, swatch pressed state, the cap name, the h1
   * suffix, the displayed price, the gallery's variant photo and the cart
   * permalinks.
   *
   * Verified against the live site: picking a package does NOT carry the
   * previously chosen cap colour over. Choosing Grey, switching to Stand Alone
   * (which moves the cap to "Standard") and coming back lands on Green, the
   * kit's first colour - not Grey. So a package click always resolves to that
   * package's first available variant.
   */
  function initProductForm() {
    var cards = all(document, '[data-variant-card]');
    if (!cards.length) return;

    var variants = [];
    var node = one(document, '[data-product-variants]');
    if (node) {
      try { variants = JSON.parse(node.textContent); } catch (error) { variants = []; }
    }
    if (!variants.length) return;

    var inputs = all(document, '[data-variant-input]');
    var actions = all(document, '.product-detail-lean-purchase__other, .purchase-cta');
    var label = one(document, '[data-buybar-label]');
    var heading = one(document, '[data-product-h1]');
    var baseTitle = '';
    if (heading) {
      baseTitle = heading.getAttribute('data-base-title');
      if (!baseTitle) {
        baseTitle = heading.textContent.split(' - ')[0].trim();
        heading.setAttribute('data-base-title', baseTitle);
      }
    }

    /**
     * The reference only has the swatch row in the document while its package is
     * selected, so the row is moved rather than shown/hidden: each unselected
     * multi-colour card carries an inert <template> copy to restore from.
     */
    function syncCapRow(pkg) {
      cards.forEach(function (card) {
        var live = one(card, '[data-cap-group]');
        var tpl = card.querySelector('template[data-cap-template]');
        var mine = card.getAttribute('data-package') === pkg;

        if (mine && !live && tpl) {
          var frag = tpl.content ? tpl.content.cloneNode(true) : null;
          if (frag) {
            var foot = one(card, '.product-detail-lean-card__foot');
            if (foot) card.insertBefore(frag, foot); else card.appendChild(frag);
            bindSwatches(card);
          }
        } else if (!mine && live) {
          if (!tpl) {
            tpl = document.createElement('template');
            tpl.setAttribute('data-cap-template', card.getAttribute('data-package') || '');
            card.appendChild(tpl);
          }
          tpl.innerHTML = live.outerHTML;
          live.parentNode.removeChild(live);
        }
      });
    }

    function bindSwatches(scope) {
      all(scope, '[data-cap-swatch]').forEach(function (sw) {
        if (sw.getAttribute('data-cap-bound') === 'true') return;
        sw.setAttribute('data-cap-bound', 'true');
        on(sw, 'click', function () {
          if (sw.disabled) return;
          select(byId(sw.getAttribute('data-cap-swatch')), true);
        });
      });
    }

    function byId(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    /** A package click resets to that package's first available variant. */
    function variantFor(pkg) {
      var i;
      for (i = 0; i < variants.length; i++) {
        if (variants[i].package === pkg && variants[i].available) return variants[i];
      }
      for (i = 0; i < variants.length; i++) {
        if (variants[i].package === pkg) return variants[i];
      }
      return null;
    }

    function groupOf(el) {
      var n = el;
      while (n && n !== document) {
        if (n.getAttribute && n.getAttribute('data-cap-group') !== null) return n.getAttribute('data-cap-group');
        n = n.parentNode;
      }
      return '';
    }

    function render(v, moveGallery) {
      cards.forEach(function (card) {
        var selected = card.getAttribute('data-package') === v.package;
        card.classList.toggle('is-selected', selected);
        var hit = one(card, '.product-detail-lean-card__hit');
        if (hit) hit.setAttribute('aria-pressed', String(selected));
      });

      syncCapRow(v.package);

      all(document, '[data-cap-swatch]').forEach(function (sw) {
        var mine = groupOf(sw) === v.package && sw.getAttribute('data-cap-value') === v.cap;
        sw.setAttribute('aria-pressed', String(mine));
      });

      all(document, '[data-cap-name]').forEach(function (n) {
        if (groupOf(n) === v.package) n.textContent = v.cap;
      });

      if (heading) heading.textContent = baseTitle + (v.package ? ' - ' + v.package : '');
      if (label) label.textContent = v.title || v.package || '';

      all(document, '.product-detail-money--current [data-money-amount]').forEach(function (n) {
        n.textContent = v.amount;
      });

      actions.forEach(function (n) { n.setAttribute('aria-disabled', String(!v.available)); });
      inputs.forEach(function (f) { f.value = v.id; });

      purchaseState.variant = v.id;
      syncPurchaseLinks();

      // Swap the variant photo that occupies the gallery's first slide.
      if (v.image) {
        ['.product-detail-gallery__static-slide img',
         '.product-detail-gallery__slide img',
         '.product-detail-gallery__thumb img'].forEach(function (sel) {
          var img = one(document, sel);
          if (img) {
            img.src = v.image;
            img.removeAttribute('srcset');
          }
        });
        if (moveGallery && productGalleryApi) productGalleryApi.goTo(0);
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      } catch (error) { /* history is optional */ }
    }

    function select(v, moveGallery) {
      if (!v) return;
      render(v, moveGallery);
    }

    cards.forEach(function (card) {
      var hit = one(card, '.product-detail-lean-card__hit') || card;
      on(hit, 'click', function () {
        select(variantFor(card.getAttribute('data-package')), true);
      });
    });

    bindSwatches(document);

    // Initial state: ?variant= wins, then whatever the server rendered.
    var startId = null;
    var requested = new URLSearchParams(window.location.search).get('variant');
    if (requested && byId(requested)) {
      startId = requested;
    } else {
      var seed = one(document, '[data-cart-add]');
      if (seed) startId = seed.getAttribute('data-cart-add');
    }
    var start = byId(startId) || variants[0];
    if (start) render(start, false);
  }

  /* ------------------------------------------------ product purchase state */

  /**
   * The reference product page buys through cart permalinks
   * (/cart/<variant>:<qty>) rather than a product form, so the chosen variant
   * and quantity live here and every purchase link is rewritten from them.
   */
  var purchaseState = {variant: null, quantity: 1, root: null};

  function syncPurchaseLinks() {
    var id = purchaseState.variant;
    if (!id) return;
    var qty = purchaseState.quantity;

    all(document, '[data-shop-pay-link], [data-other-link]').forEach(function (link) {
      var root = link.getAttribute('data-cart-root');
      if (!root) {
        // "/cart/123:1" -> "/cart"; captured once so repeated syncs stay stable.
        root = (link.getAttribute('href') || '').split('?')[0].replace(/\/\d+:\d+$/, '');
        link.setAttribute('data-cart-root', root);
      }
      var suffix = link.hasAttribute('data-shop-pay-link') ? '?payment=shop_pay' : '';
      link.setAttribute('href', root + '/' + id + ':' + qty + suffix);
    });

    all(document, '[data-cart-add]').forEach(function (link) {
      link.setAttribute('data-cart-add', id);
    });
  }

  /** Quantity stepper in the buy panel. */
  function initQuantity() {
    var stepper = one(document, '[data-quantity-stepper]');
    if (!stepper) return;

    var readout = one(stepper, '[data-quantity-value]');
    var buttons = all(stepper, '[data-quantity-step]');

    function render() {
      if (readout) readout.textContent = String(purchaseState.quantity);
      all(document, '[data-quantity-input]').forEach(function (field) {
        field.value = String(purchaseState.quantity);
      });
      buttons.forEach(function (button) {
        var step = parseInt(button.getAttribute('data-quantity-step'), 10) || 0;
        button.disabled = purchaseState.quantity + step < 1;
      });
      syncPurchaseLinks();
    }

    buttons.forEach(function (button) {
      on(button, 'click', function () {
        var step = parseInt(button.getAttribute('data-quantity-step'), 10) || 0;
        purchaseState.quantity = Math.max(1, purchaseState.quantity + step);
        render();
      });
    });

    render();
  }

  /** "What's included" sends people to the Package tab further down the page. */
  function initIncludedJump() {
    var triggers = all(document, '[data-included-jump]');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      on(trigger, 'click', function () {
        var panel = one(document, '#product-detail-panel-package');
        var sections = all(document, '.product-detail-tabs__section');
        var tabs = all(document, '.product-detail-tabs__bar button');
        var index = sections.indexOf(panel);
        if (index > -1 && tabs[index]) {
          tabs[index].click();                      // reuses the tab enhancer
          return;
        }
        if (panel) {
          panel.scrollIntoView({
            behavior: reducedMotion() ? 'auto' : 'smooth',
            block: 'start',
          });
        }
      });
    });
  }

  /**
   * Upgrades the permalink purchase anchors into an AJAX add plus the cart
   * drawer. Without JavaScript the bare href still adds the item and lands on
   * /cart, so the control works either way.
   */
  function initCartAddLinks() {
    all(document, '[data-cart-add]').forEach(function (link) {
      on(link, 'click', function (event) {
        if (link.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        var drawer = one(document, '[data-cart-overlay]');
        if (!drawer) return;                     // no drawer: follow the link
        event.preventDefault();
        link.setAttribute('aria-busy', 'true');

        var body = new FormData();
        body.append('id', link.getAttribute('data-cart-add'));
        body.append('quantity', String(purchaseState.quantity));

        fetch('/cart/add.js', {method: 'POST', body: body})
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            return response.json();
          })
          .then(function () { return refreshCartSection(); })
          .then(function () { openCart(); })
          .catch(function () { window.location.href = link.getAttribute('href'); })
          .finally(function () { link.removeAttribute('aria-busy'); });
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    initNavigation();
    initAnnouncement();
    initCart();
    initAddToCart();
    initSearch();
    initHeroPromo();
    initHomeVideo();
    initShowUp();
    initWorkoutCompanion();
    initCustomerVideos();
    initProductGallery();

    // Seed the permalink state before the controls that rewrite it run.
    var seed = one(document, '[data-cart-add]');
    if (seed) purchaseState.variant = seed.getAttribute('data-cart-add');
    purchaseState.quantity = 1;

    initProductForm();
    initQuantity();
    initIncludedJump();
    initCartAddLinks();

    all(document, '[data-track-carousel]').forEach(initTrackCarousel);

    ENHANCERS.forEach(function (enhance) {
      try {
        enhance(document);
      } catch (error) {
        // One widget failing must never take the rest of the page down.
        if (window.console) console.warn('[bodypark] enhancer failed', error);
      }
    });
  }

  function teardown() {
    cleanups.forEach(function (fn) {
      try { fn(); } catch (error) {}
    });
    cleanups = [];
  }

  function boot() {
    teardown();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Keep sections interactive while they are edited in the theme editor.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
